/**
 * Resolve a {@link SkeletonHeightConstraint} into absolute world-Z bounds for
 * skeleton 3D extrusion.
 *
 * A skeleton lives on one floor. Each node and edge carries an independent
 * height constraint with a `top` and `bottom` bound. Each bound is:
 *  - `'default'`  → for `top`: the next floor above the skeleton's floor (or
 *                   this floor's ceiling / a fixed wall height when there is
 *                   none). For `bottom`: the skeleton's own floor height.
 *  - `{ targetFloorId }` → that floor's height.
 * In both cases the bound's `offset` (metres) is added. Offsets are ALWAYS
 * applied, even when no floor groups are known.
 */

import { SkeletonHeightBound, SkeletonHeightConstraint, DEFAULT_SKELETON_HEIGHT_CONSTRAINT, FALLBACK_INTERIOR_HEIGHT_M } from '@pointorama/potree-shared';

/** Minimal floor-group shape the resolver needs (subset of FloorGroupData). */
export interface FloorHeightInfo {
  groupIdentifier: string;
  /** Floor plane height (world Z). */
  floorHeight: number;
  /** Ceiling plane height (world Z), if the floor group has a paired ceiling. */
  ceilingHeight?: number;
}

/**
 * Fallback wall height (metres) used for a `default` top bound when no next
 * floor and no ceiling is known for the skeleton's floor. Keeps a sane default
 * rather than collapsing to the floor plane. Aliases the shared
 * FALLBACK_INTERIOR_HEIGHT_M so wall, IFC and isolation fallbacks agree.
 */
export const FALLBACK_WALL_HEIGHT = FALLBACK_INTERIOR_HEIGHT_M;

/** The nearest floor strictly above `currentFloorHeight`, or null. */
function nextFloorAbove(floors: readonly FloorHeightInfo[], currentFloorHeight: number): number | null {
  let next: number | null = null;
  for (const f of floors) {
    if (f.floorHeight > currentFloorHeight + 1e-4) {
      if (next === null || f.floorHeight < next) next = f.floorHeight;
    }
  }
  return next;
}

/** The skeleton's own floor group (matched by height), if present. */
function currentFloor(floors: readonly FloorHeightInfo[], currentFloorHeight: number): FloorHeightInfo | undefined {
  return floors.find((f) => Math.abs(f.floorHeight - currentFloorHeight) < 1e-3);
}

/**
 * Resolve one bound to an absolute world-Z.
 *
 * @param bound  The bound (missing → default for its side).
 * @param side   Which bound this is — changes what `default` references.
 * @param floors All floor groups in the project (heights in world Z).
 * @param currentFloorHeight  The skeleton's own floor height.
 */
export function resolveSkeletonBound(bound: SkeletonHeightBound | undefined, side: 'top' | 'bottom', floors: readonly FloorHeightInfo[], currentFloorHeight: number): number {
  const b = bound ?? (side === 'top' ? DEFAULT_SKELETON_HEIGHT_CONSTRAINT.top : DEFAULT_SKELETON_HEIGHT_CONSTRAINT.bottom);
  const mode = b.mode;

  let base: number;
  if (mode === 'default') {
    if (side === 'bottom') {
      // Bottom default = this floor's own plane.
      base = currentFloorHeight;
    } else {
      // Top default: THIS floor's own ceiling — under true-ceiling semantics
      // walls deliberately stop at the detected ceiling (the slab gap up to the
      // next floor is intended). Only when the floor has no ceiling do we fall
      // back to the next floor up (legacy multi-storey stacking), then a fixed
      // wall height.
      const cur = currentFloor(floors, currentFloorHeight);
      if (cur?.ceilingHeight != null) {
        base = cur.ceilingHeight;
      } else {
        const next = nextFloorAbove(floors, currentFloorHeight);
        base = next ?? currentFloorHeight + FALLBACK_WALL_HEIGHT;
      }
    }
  } else {
    const target = floors.find((f) => f.groupIdentifier === mode.targetFloorId);
    // Unknown target floor → fall back to this floor's plane so the offset is
    // still meaningful rather than silently dropping the constraint.
    base = target ? target.floorHeight : currentFloorHeight;
  }

  return base + (b.offset ?? 0);
}

/**
 * Resolve a full constraint into `{ bottom, top }` absolute world-Z values.
 * `top` is clamped to be at least slightly above `bottom` so the extrusion
 * never inverts or collapses.
 */
export function resolveSkeletonConstraint(constraint: SkeletonHeightConstraint | undefined, floors: readonly FloorHeightInfo[], currentFloorHeight: number): { bottom: number; top: number } {
  const c = constraint ?? DEFAULT_SKELETON_HEIGHT_CONSTRAINT;
  const bottom = resolveSkeletonBound(c.bottom, 'bottom', floors, currentFloorHeight);
  let top = resolveSkeletonBound(c.top, 'top', floors, currentFloorHeight);
  if (top <= bottom + 1e-3) top = bottom + 1e-3;
  return { bottom, top };
}
